import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Ruler, Loader2, Plus, TrendingDown, TrendingUp, Minus,
} from "lucide-react";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import ShareButton from "../components/ShareButton";

const FIELDS = [
  { key: "chest_cm", label: "Chest" },
  { key: "waist_cm", label: "Waist" },
  { key: "hips_cm",  label: "Hips" },
  { key: "arms_cm",  label: "Arms" },
];

const fmt = (v) => (v == null ? "—" : `${Math.round(v * 10) / 10} cm`);

export default function MeasurementsPage() {
  const [history, setHistory] = useState([]);
  const [deltas, setDeltas] = useState({});
  const [latest, setLatest] = useState({});
  const [form, setForm] = useState({ chest_cm: "", waist_cm: "", hips_cm: "", arms_cm: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [h, l] = await Promise.all([
        api.get("/measurements/history?days=180"),
        api.get("/measurements/latest"),
      ]);
      setHistory(h.data.items || []);
      setDeltas(h.data.deltas || {});
      setLatest(l.data || {});
    } catch (err) {
      console.error("measurements load failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    FIELDS.forEach(({ key }) => {
      if (form[key] !== "" && form[key] != null) payload[key] = Number(form[key]);
    });
    if (Object.keys(payload).length === 0) {
      toast.error("Enter at least one measurement");
      return;
    }
    setSaving(true);
    try {
      await api.post("/measurements", payload);
      setForm({ chest_cm: "", waist_cm: "", hips_cm: "", arms_cm: "" });
      await load();
      toast.success("Measurements logged");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const chartData = (key) =>
    history
      .filter((it) => it[key] != null)
      .map((it) => ({
        label: new Date(it.logged_on).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        value: it[key],
      }));

  const deltaIcon = (d) => {
    if (d == null || d === 0) return <Minus className="w-3 h-3" />;
    return d < 0 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />;
  };

  // Build a single share-friendly summary line if user has progress
  const shareSummary = (() => {
    const wins = [];
    FIELDS.forEach(({ key, label }) => {
      const d = deltas[key];
      if (d == null) return;
      if (Math.abs(d) >= 1) {
        const sign = d < 0 ? "-" : "+";
        wins.push(`${label} ${sign}${Math.abs(d)}cm`);
      }
    });
    return wins;
  })();

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-up" data-testid="measurements-page">
      <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow text-[#6B9BFF] mb-2 flex items-center gap-2">
            <Ruler className="w-3.5 h-3.5" /> Measurements
          </div>
          <h1 className="font-display text-4xl sm:text-5xl leading-none">Track the tape.</h1>
          <p className="text-white/60 mt-3 max-w-xl">
            Bodyweight only tells half the story. Log chest, waist, hips and arms regularly to see
            where the gains (and losses) really show.
          </p>
        </div>
        {shareSummary.length > 0 && (
          <ShareButton
            variant="button"
            text={`My DAIONE progress: ${shareSummary.join(" · ")} 🎯`}
            title="My body measurement progress"
            testid="share-measurements"
          />
        )}
      </div>

      {/* Log form */}
      <form
        onSubmit={submit}
        className="tracing-border p-6 mb-8"
        data-testid="measurements-form"
      >
        <div className="label-eyebrow text-[#6B9BFF] mb-4">Log today</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {FIELDS.map(({ key, label }) => (
            <div key={key}>
              <label className="label-eyebrow text-white/50 block mb-2 text-[10px]">{label} (cm)</label>
              <input
                type="number"
                step="0.1"
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                placeholder={latest[key] != null ? String(latest[key]) : "—"}
                className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#6B9BFF]"
                data-testid={`measurement-input-${key}`}
              />
            </div>
          ))}
        </div>
        <button
          type="submit"
          disabled={saving}
          className="mt-4 bg-[#6B9BFF] text-black font-black uppercase tracking-widest px-6 py-3 hover:bg-white transition-colors inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="measurements-submit"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Save measurements
        </button>
        {Object.keys(latest).length > 0 && (
          <div className="mt-3 text-xs text-white/40">
            Same-day entries merge — partial logs accumulate.
          </div>
        )}
      </form>

      {/* Per-metric cards */}
      {loading ? (
        <div className="flex items-center gap-2 text-white/50 text-sm py-12">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {FIELDS.map(({ key, label }) => {
            const data = chartData(key);
            const current = latest[key];
            const delta = deltas[key];
            const trendColor =
              delta == null
                ? "text-white/40"
                : delta < 0
                ? "text-[#6B9BFF]"
                : delta > 0
                ? "text-[#FFB020]"
                : "text-white/40";
            return (
              <article
                key={key}
                className="border border-white/10 bg-[#121212] p-6"
                data-testid={`measurement-card-${key}`}
              >
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div>
                    <div className="label-eyebrow text-white/50">{label}</div>
                    <div className="font-display text-4xl mt-1">{fmt(current)}</div>
                    {delta != null && (
                      <div
                        className={`text-xs mt-2 inline-flex items-center gap-1 ${trendColor}`}
                        data-testid={`measurement-delta-${key}`}
                      >
                        {deltaIcon(delta)}
                        {delta === 0 ? "no change" : `${delta > 0 ? "+" : ""}${delta} cm vs first log`}
                      </div>
                    )}
                  </div>
                  {data.length >= 2 && Math.abs(delta || 0) >= 1 && (
                    <ShareButton
                      text={`${label} progress on DAIONE: ${delta > 0 ? "+" : ""}${delta} cm 💪`}
                      title={`${label} progress`}
                      testid={`share-${key}`}
                      card={{
                        type: "measurement",
                        payload: {
                          area: label,
                          latest_cm: current,
                          delta_cm: delta,
                          since: data[0]?.date,
                        },
                      }}
                    />
                  )}
                </div>
                <div className="h-40">
                  {data.length < 2 ? (
                    <div className="h-full flex items-center justify-center text-white/30 text-xs italic">
                      Log at least 2 entries to see the trend.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data}>
                        <defs>
                          <linearGradient id={`fill-${key}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#6B9BFF" stopOpacity={0.4} />
                            <stop offset="100%" stopColor="#6B9BFF" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="label" stroke="#71717A" fontSize={10} />
                        <YAxis stroke="#71717A" fontSize={10} domain={["dataMin - 2", "dataMax + 2"]} width={32} />
                        <Tooltip
                          contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }}
                          formatter={(v) => [`${v} cm`, label]}
                        />
                        <Area type="monotone" dataKey="value" stroke="#6B9BFF" strokeWidth={2.5} fill={`url(#fill-${key})`} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
